import { c as _c } from "react/compiler-runtime";
/**
 * Transcription Queue Store
 *
 * Persistent queue for audio chunk transcription with:
 * - IndexedDB blob storage
 * - Automatic retry with exponential backoff
 * - Error isolation (one failure doesn't block others)
 * - Recovery on page load
 */
import posthog from "posthog-js";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { logTimestamp } from "@/lib/transcription-queue";
import { TRANSCRIPTION_QUEUE_BLOB_MAX_AGE_MS, deleteBlob, deleteOldBlobs, getAllBlobKeys, getBlob, isIndexedDBAvailable, requestPersistentStorage, setAggressivePruneListener, storeBlob } from "@/lib/transcription-queue/idb";
import { MAX_RETRIES, calculateNextRetryTime, isReadyForProcessing, processItem, sortByProcessingPriority } from "@/lib/transcription-queue/processor";
import type { EnqueueParams, ProcessChunkFn, TranscriptionQueueItem, TranscriptionQueueItemStatus } from "@/lib/transcription-queue/types";
import { createTenantStorage } from "./tenant-storage-helpers";
const LOG_PREFIX = () => `[${logTimestamp()}][TranscriptionQueue:Store]`;
interface TranscriptionQueueState {
  /** Queue items (metadata only - blobs in IndexedDB) */
  items: Record<string, TranscriptionQueueItem>;

  /** Whether the processor is currently running */
  isProcessing: boolean;

  /** ID of the item currently being processed */
  currentItemId: string | null;

  /** The chunk processor function (set by transcription hook) */
  processFn: ProcessChunkFn | null;

  /** Actions */
  setProcessFn: (fn: ProcessChunkFn) => void;
  enqueue: (params: EnqueueParams) => Promise<string>;
  processQueue: () => Promise<void>;
  initializeFromStorage: () => Promise<void>;
  /** Drop IndexedDB blobs and queue rows older than TRANSCRIPTION_QUEUE_BLOB_MAX_AGE_MS (3 days). */
  pruneStaleQueueData: () => Promise<void>;
  retryAllFailed: () => void;
  clearNote: (noteId: string) => Promise<void>;
  getItemsForNote: (noteId: string) => TranscriptionQueueItem[];
  clearCompletedForNote: (noteId: string) => void;

  /** Internal actions */
  _markProcessing: (id: string) => void;
  _markCompleted: (id: string) => void;
  _markFailed: (id: string, error: string, permanent: boolean) => void;
  _scheduleRetry: (id: string) => void;
  _removeItem: (id: string) => void;

  /** Computed values */
  getPendingCount: () => number;
  getFailedCount: () => number;
}

// Generate a simple unique ID
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

// Retry timer reference
let retryTimerId: ReturnType<typeof setTimeout> | null = null;
export const useTranscriptionQueue = create<TranscriptionQueueState>()(persist((set, get) => ({
  items: {},
  isProcessing: false,
  currentItemId: null,
  processFn: null,
  setProcessFn: fn => set({
    processFn: fn
  }),
  enqueue: async params => {
    const id = generateId();
    const blobKey = `blob-${id}`;
    console.log(`${LOG_PREFIX()} Enqueueing chunk: id=${id}, noteId=${params.noteId}, size=${params.blob.size} bytes`);

    // Store blob in IndexedDB
    if (isIndexedDBAvailable()) {
      try {
        await storeBlob(blobKey, params.blob, params.noteId);
      } catch (err) {
        console.error(`${LOG_PREFIX()} Failed to store blob in IndexedDB:`, err);
        posthog.capture("transcription_indexeddb_error", {
          note_id: params.noteId,
          blob_size: params.blob.size,
          error_message: err instanceof Error ? err.message : String(err)
        });
        throw err;
      }
    } else {
      console.warn(`${LOG_PREFIX()} IndexedDB not available - chunk will not persist across refreshes`);
    }
    const item: TranscriptionQueueItem = {
      id,
      noteId: params.noteId,
      patientId: params.patientId,
      providerId: params.providerId,
      locationId: params.locationId,
      mimeType: params.mimeType,
      blobKey,
      blobSize: params.blob.size,
      status: "pending",
      retryCount: 0,
      createdAt: Date.now()
    };
    set(state => ({
      items: {
        ...state.items,
        [id]: item
      }
    }));
    const queueSize = Object.keys(get().items).length;
    console.log(`${LOG_PREFIX()} Chunk enqueued: id=${id}, queueSize=${queueSize}`);
    posthog.capture("transcription_chunk_enqueued", {
      queue_item_id: id,
      note_id: params.noteId,
      patient_id: params.patientId,
      provider_id: params.providerId,
      location_id: params.locationId,
      blob_size: params.blob.size,
      mime_type: params.mimeType,
      queue_size: queueSize
    });

    // Trigger processing (non-blocking) - only if processor is available
    // If not available yet, items will be processed when TranscriptionQueueInitializer
    // sets the processor and calls processQueue()
    if (get().processFn) {
      void get().processQueue();
    }
    return id;
  },
  processQueue: async () => {
    const state = get();

    // Single-processor pattern
    if (state.isProcessing) {
      console.log(`${LOG_PREFIX()} processQueue: Already processing, skipping`);
      return;
    }
    const processFn = state.processFn;
    if (!processFn) {
      // This can happen during initialization - items will be processed
      // when the processor is set via setProcessFn
      return;
    }
    const queueSize = Object.keys(state.items).length;
    console.log(`${LOG_PREFIX()} processQueue: Starting, queueSize=${queueSize}`);
    set({
      isProcessing: true
    });
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const currentState = get();
        const allItems = Object.values(currentState.items);

        // Find items ready for processing
        const readyItems = allItems.filter(isReadyForProcessing);
        if (readyItems.length === 0) {
          console.log(`${LOG_PREFIX()} processQueue: No ready items, stopping`);
          break;
        }

        // Filter out items that are blocked by earlier chunks for the same note
        // A chunk can only process if all earlier chunks (by createdAt) have completed
        const unlockedItems = readyItems.filter(item => {
          const earlierChunks = allItems.filter(other => other.noteId === item.noteId && other.createdAt < item.createdAt && other.status !== "permanent_failure" &&
          // permanent failures don't block
          other.status !== "completed" // completed items don't block
          );
          if (earlierChunks.length > 0) {
            console.log(`${LOG_PREFIX()} Item ${item.id} blocked by ${earlierChunks.length} earlier chunks for note ${item.noteId}`);
            return false;
          }
          return true;
        });
        if (unlockedItems.length === 0) {
          console.log(`${LOG_PREFIX()} processQueue: ${readyItems.length} items ready but all blocked by earlier chunks`);
          break;
        }
        console.log(`${LOG_PREFIX()} processQueue: ${unlockedItems.length} items ready and unlocked`);

        // Sort by priority and get the next item
        const sorted = sortByProcessingPriority(unlockedItems);
        const item = sorted[0];
        console.log(`${LOG_PREFIX()} processQueue: Processing item id=${item.id}, noteId=${item.noteId}`);

        // Mark as processing
        get()._markProcessing(item.id);

        // Get blob from IndexedDB
        let blob: Blob | undefined;
        try {
          blob = await getBlob(item.blobKey);
        } catch (err) {
          console.error(`${LOG_PREFIX()} Failed to get blob from IndexedDB:`, err);
        }
        if (!blob) {
          // Blob missing - mark as permanent failure
          console.error(`${LOG_PREFIX()} Blob missing for item id=${item.id}`);
          get()._markFailed(item.id, "Audio blob not found", true);
          continue;
        }
        console.log(`${LOG_PREFIX()} Processing chunk for noteId=${item.noteId}`);

        // Process the item - backend looks up transcript by note_id
        const outcome = await processItem(item, blob, processFn);
        if (outcome.success) {
          const processingDurationMs = Date.now() - (item.lastAttemptAt || item.createdAt);
          console.log(`${LOG_PREFIX()} Item completed: id=${item.id}, transcriptId=${outcome.transcriptId}`);
          get()._markCompleted(item.id);
          posthog.capture("transcription_chunk_completed", {
            queue_item_id: item.id,
            note_id: item.noteId,
            patient_id: item.patientId,
            provider_id: item.providerId,
            location_id: item.locationId,
            transcript_id: outcome.transcriptId,
            blob_size: item.blobSize,
            retry_count: item.retryCount,
            processing_duration_ms: processingDurationMs,
            total_duration_ms: Date.now() - item.createdAt
          });

          // Delete blob immediately - audio is already stored in GCS
          void deleteBlob(item.blobKey).catch(console.error);
        } else if (outcome.permanent) {
          console.error(`${LOG_PREFIX()} Item permanently failed: id=${item.id}, error=${outcome.error}`);
          get()._markFailed(item.id, outcome.error, true);
          posthog.capture("transcription_chunk_permanent_failure", {
            queue_item_id: item.id,
            note_id: item.noteId,
            patient_id: item.patientId,
            provider_id: item.providerId,
            location_id: item.locationId,
            blob_size: item.blobSize,
            retry_count: item.retryCount,
            error_message: outcome.error,
            total_duration_ms: Date.now() - item.createdAt
          });

          // Delete blob for permanent failures
          void deleteBlob(item.blobKey).catch(console.error);
        } else {
          console.warn(`${LOG_PREFIX()} Item failed (will retry): id=${item.id}, error=${outcome.error}`);
          posthog.capture("transcription_chunk_failed", {
            queue_item_id: item.id,
            note_id: item.noteId,
            patient_id: item.patientId,
            provider_id: item.providerId,
            location_id: item.locationId,
            blob_size: item.blobSize,
            retry_count: item.retryCount,
            error_message: outcome.error,
            will_retry: item.retryCount < MAX_RETRIES
          });

          // Transient failure - schedule retry
          get()._scheduleRetry(item.id);
        }
      }

      // Schedule next retry check if there are failed items
      const finalState = get();
      const failedItems = Object.values(finalState.items).filter(i => i.status === "failed" && i.nextRetryAt);
      if (failedItems.length > 0) {
        // Find the earliest retry time
        const nextRetryAt = Math.min(...failedItems.map(i => i.nextRetryAt!));
        const delay = Math.max(0, nextRetryAt - Date.now());
        console.log(`${LOG_PREFIX()} Scheduling retry check in ${Math.round(delay / 1000)}s for ${failedItems.length} failed items`);

        // Clear any existing timer
        if (retryTimerId) {
          clearTimeout(retryTimerId);
        }
        retryTimerId = setTimeout(() => {
          console.log(`${LOG_PREFIX()} Retry timer fired, resuming processing`);
          retryTimerId = null;
          void get().processQueue();
        }, delay);
      }
    } finally {
      console.log(`${LOG_PREFIX()} processQueue: Finished`);
      set({
        isProcessing: false,
        currentItemId: null
      });
    }
  },
  initializeFromStorage: async () => {
    const state = get();
    const allItems = Object.values(state.items);
    const itemCount = allItems.length;
    console.log(`${LOG_PREFIX()} Initializing from storage: ${itemCount} items found`);
    if (isIndexedDBAvailable()) {
      void requestPersistentStorage();
      // Aggressive pressure pruning in idb.ts drops blobs older than 24h,
      // but queue metadata is retained for 3 days. Without this listener
      // the store would hold "zombie" items whose blobs no longer exist
      // and fail with "Audio blob not found" when the processor reaches
      // them. Register after the store exists so the closure can call set.
      setAggressivePruneListener(deletedKeys => {
        const keySet = new Set(deletedKeys);
        set(s => {
          const newItems = {
            ...s.items
          };
          let removed = 0;
          for (const [id, item] of Object.entries(newItems)) {
            if (keySet.has(item.blobKey)) {
              delete newItems[id];
              removed++;
            }
          }
          if (removed > 0) {
            console.log(`${LOG_PREFIX()} Removed ${removed} queue item(s) after aggressive blob prune`);
          }
          return removed > 0 ? {
            items: newItems
          } : s;
        });
      });
    }

    // Collect stats for PostHog
    const pendingCount = allItems.filter(i => i.status === "pending").length;
    const processingCount = allItems.filter(i => i.status === "processing").length;
    const failedCount = allItems.filter(i => i.status === "failed").length;
    const permanentFailureCount = allItems.filter(i => i.status === "permanent_failure").length;

    // Reset any "processing" items to "pending" (crash recovery)
    // and remove any "completed" items left over from a previous session
    const updates: Record<string, TranscriptionQueueItem> = {};
    const completedIds: string[] = [];
    let hasUpdates = false;
    for (const [id, item] of Object.entries(state.items)) {
      if (item.status === "processing") {
        console.log(`${LOG_PREFIX()} Recovering interrupted item: id=${id}`);
        updates[id] = {
          ...item,
          status: "pending" as TranscriptionQueueItemStatus
        };
        hasUpdates = true;
      } else if (item.status === "completed") {
        completedIds.push(id);
      }
    }
    if (hasUpdates) {
      console.log(`${LOG_PREFIX()} Reset ${Object.keys(updates).length} interrupted items to pending`);
      set(s => ({
        items: {
          ...s.items,
          ...updates
        }
      }));
    }
    if (completedIds.length > 0) {
      console.log(`${LOG_PREFIX()} Cleaning up ${completedIds.length} completed items from previous session`);
      set(s => {
        const newItems = {
          ...s.items
        };
        for (const id of completedIds) {
          delete newItems[id];
        }
        return {
          items: newItems
        };
      });
    }
    await get().pruneStaleQueueData();

    // Track queue recovery if there were any pending items
    if (itemCount > 0) {
      posthog.capture("transcription_queue_recovered", {
        total_items: itemCount,
        pending_count: pendingCount,
        processing_count: processingCount,
        failed_count: failedCount,
        permanent_failure_count: permanentFailureCount,
        interrupted_count: Object.keys(updates).length
      });
    }

    // Verify blobs exist for pending/failed items
    if (isIndexedDBAvailable()) {
      try {
        const blobKeys = new Set(await getAllBlobKeys());
        console.log(`${LOG_PREFIX()} Found ${blobKeys.size} blobs in IndexedDB`);
        const currentItems = get().items;
        const orphanedItemIds: string[] = [];
        for (const [id, item] of Object.entries(currentItems)) {
          if ((item.status === "pending" || item.status === "failed") && !blobKeys.has(item.blobKey)) {
            orphanedItemIds.push(id);
          }
        }

        // Remove orphaned items
        if (orphanedItemIds.length > 0) {
          console.warn(`${LOG_PREFIX()} Removing ${orphanedItemIds.length} orphaned items (blobs missing)`);
          set(s => {
            const newItems = {
              ...s.items
            };
            for (const id of orphanedItemIds) {
              delete newItems[id];
            }
            return {
              items: newItems
            };
          });
        }
      } catch (err) {
        console.error("Failed to verify blobs during initialization:", err);
      }
    }
  },
  pruneStaleQueueData: async () => {
    const cutoff = Date.now() - TRANSCRIPTION_QUEUE_BLOB_MAX_AGE_MS;
    if (isIndexedDBAvailable()) {
      try {
        const deletedKeys = await deleteOldBlobs(TRANSCRIPTION_QUEUE_BLOB_MAX_AGE_MS);
        if (deletedKeys.length > 0) {
          console.log(`${LOG_PREFIX()} Removed ${deletedKeys.length} IndexedDB blob(s) older than retention`);
        }
      } catch (err) {
        console.error(`${LOG_PREFIX()} Failed to delete stale blobs from IndexedDB:`, err);
      }
    }
    const items = get().items;
    const staleIds = Object.entries(items).filter(([, item]) => item.createdAt < cutoff).map(([id]) => id);
    if (staleIds.length === 0) {
      return;
    }
    console.log(`${LOG_PREFIX()} Removing ${staleIds.length} queue item(s) older than retention`);
    set(s => {
      const newItems = {
        ...s.items
      };
      for (const id of staleIds) {
        delete newItems[id];
      }
      return {
        items: newItems
      };
    });
  },
  retryAllFailed: () => {
    const failedCount = Object.values(get().items).filter(i => i.status === "failed" || i.status === "permanent_failure").length;
    console.log(`${LOG_PREFIX()} Retrying all failed items: count=${failedCount}`);
    set(state => {
      const newItems = {
        ...state.items
      };
      for (const [id, item] of Object.entries(newItems)) {
        if (item.status === "failed" || item.status === "permanent_failure") {
          newItems[id] = {
            ...item,
            status: "pending",
            retryCount: 0,
            lastError: undefined,
            nextRetryAt: undefined
          };
        }
      }
      return {
        items: newItems
      };
    });

    // Trigger processing
    void get().processQueue();
  },
  clearNote: async noteId => {
    const state = get();
    const noteItems = Object.values(state.items).filter(i => i.noteId === noteId);
    console.log(`${LOG_PREFIX()} Clearing ${noteItems.length} items for note ${noteId}`);

    // Delete blobs
    for (const item of noteItems) {
      try {
        await deleteBlob(item.blobKey);
      } catch (err) {
        console.error("Failed to delete blob:", err);
      }
    }

    // Remove items
    set(s => {
      const newItems = {
        ...s.items
      };
      for (const item of noteItems) {
        delete newItems[item.id];
      }
      return {
        items: newItems
      };
    });
  },
  getItemsForNote: noteId => {
    return Object.values(get().items).filter(i => i.noteId === noteId);
  },
  clearCompletedForNote: noteId => {
    set(state => {
      const newItems = {
        ...state.items
      };
      for (const [id, item] of Object.entries(newItems)) {
        if (item.noteId === noteId && item.status === "completed") {
          delete newItems[id];
        }
      }
      return {
        items: newItems
      };
    });
  },
  // Internal actions
  _markProcessing: id => {
    set(state => ({
      currentItemId: id,
      items: {
        ...state.items,
        [id]: {
          ...state.items[id],
          status: "processing",
          lastAttemptAt: Date.now()
        }
      }
    }));
  },
  _markCompleted: id => {
    set(state => {
      const item = state.items[id];
      if (!item) return state;
      return {
        items: {
          ...state.items,
          [id]: {
            ...item,
            status: "completed"
          }
        }
      };
    });
  },
  _markFailed: (id, error, permanent) => {
    set(state => ({
      items: {
        ...state.items,
        [id]: {
          ...state.items[id],
          status: permanent ? "permanent_failure" : "failed",
          lastError: error
        }
      }
    }));
  },
  _scheduleRetry: id => {
    const item = get().items[id];
    if (!item) return;
    const newRetryCount = item.retryCount + 1;
    if (newRetryCount > MAX_RETRIES) {
      // Max retries exceeded - mark as permanent failure
      console.error(`${LOG_PREFIX()} Max retries exceeded for item: id=${id}, retries=${newRetryCount}`);
      posthog.capture("transcription_chunk_max_retries_exceeded", {
        queue_item_id: id,
        note_id: item.noteId,
        patient_id: item.patientId,
        provider_id: item.providerId,
        location_id: item.locationId,
        blob_size: item.blobSize,
        retry_count: newRetryCount,
        last_error: item.lastError,
        total_duration_ms: Date.now() - item.createdAt
      });
      set(state => ({
        items: {
          ...state.items,
          [id]: {
            ...item,
            status: "permanent_failure",
            lastError: `Max retries (${MAX_RETRIES}) exceeded`,
            retryCount: newRetryCount
          }
        }
      }));
      return;
    }
    const nextRetryAt = calculateNextRetryTime(newRetryCount);
    posthog.capture("transcription_chunk_retry_scheduled", {
      queue_item_id: id,
      note_id: item.noteId,
      patient_id: item.patientId,
      provider_id: item.providerId,
      retry_count: newRetryCount,
      last_error: item.lastError,
      next_retry_at: nextRetryAt,
      retry_delay_ms: nextRetryAt - Date.now()
    });
    set(state => ({
      items: {
        ...state.items,
        [id]: {
          ...item,
          status: "failed",
          retryCount: newRetryCount,
          nextRetryAt
        }
      }
    }));
  },
  _removeItem: id => {
    set(state => {
      const newItems = {
        ...state.items
      };
      delete newItems[id];
      return {
        items: newItems
      };
    });
  },
  // Computed values
  getPendingCount: () => {
    return Object.values(get().items).filter(i => i.status === "pending" || i.status === "processing").length;
  },
  getFailedCount: () => {
    return Object.values(get().items).filter(i => i.status === "failed" || i.status === "permanent_failure").length;
  }
}), {
  name: "transcription-queue",
  storage: createTenantStorage(),
  partialize: state => ({
    // Only persist queue items
    items: state.items
    // Don't persist: isProcessing, currentItemId, processFn (runtime state)
  }),
  version: 3 // Bump version - sessionId removed
}));

/**
 * Hook to get queue status for UI display.
 */
export function useTranscriptionQueueStatus() {
  const $ = _c(7);
  const pendingCount = useTranscriptionQueue(_temp);
  const failedCount = useTranscriptionQueue(_temp2);
  const isProcessing = useTranscriptionQueue(_temp3);
  const retryAllFailed = useTranscriptionQueue(_temp4);
  const t0 = pendingCount > 0;
  const t1 = failedCount > 0;
  let t2;
  if ($[0] !== failedCount || $[1] !== isProcessing || $[2] !== pendingCount || $[3] !== retryAllFailed || $[4] !== t0 || $[5] !== t1) {
    t2 = {
      hasPending: t0,
      hasFailed: t1,
      pendingCount,
      failedCount,
      isProcessing,
      retryAllFailed
    };
    $[0] = failedCount;
    $[1] = isProcessing;
    $[2] = pendingCount;
    $[3] = retryAllFailed;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function _temp4(s_2) {
  return s_2.retryAllFailed;
}
function _temp3(s_1) {
  return s_1.isProcessing;
}
function _temp2(s_0) {
  return s_0.getFailedCount();
}
function _temp(s) {
  return s.getPendingCount();
}